import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./new-patients.module.css";
import { HoverableImageLink } from "~/components/about-us/HoverableImageLink";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";

type Localization = {
  title: string;
  introHeading: string;
  introContent: string;
  whyChooseUs: string;
  yourFirstVisit: string;
  freeConsultation: string;
  insuranceAndFinance: string;
};

const englishLocalization: Localization = {
  title: "New Patients",
  introHeading: "Our Family Can’t Wait to Transform Your Smile",
  introContent:
    "As a member of our Miami Orthodontic Specialists family, you’ll get the best possible treatment using the best technology.",
  whyChooseUs: "Why Choose Us?",
  yourFirstVisit: "Your First Visit",
  freeConsultation: "Free Consult",
  insuranceAndFinance: "Insurance & Finances",
};

const spanishLocalization: Localization = {
  title: "Nuevos Pacientes",
  introHeading: "Nuestra familia no puede esperar para transformar tu sonrisa",
  introContent:
    "Como miembro de nuestra familia de especialistas en ortodoncia de Miami, recibirá el mejor tratamiento posible utilizando la mejor tecnología.",
  whyChooseUs: "Por qué elegirnos",
  yourFirstVisit: "Su primera visita",
  freeConsultation: "Consulta gratuita",
  insuranceAndFinance: "Seguro y Finanzas",
};

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function NewPatientsPage() {
  const getLocalization = useLocalization(localized);
  return (
    <>
      <HeroStyle2
        heroImageUrl="/miami-ortho-1.jpg"
        title={getLocalization("title")}
        backgroundPosition="center top"
      />
      <Flex
        className={`section ${styles.introSection}`}
        width="100%"
        align="center"
        justify="center"
      >
        <Flex direction="column" gap="7" className={`innerSection`} p="5">
          <Heading as="h2" className={styles.heading} weight="bold">
            {getLocalization("introHeading")}
          </Heading>
          <Text
            size="5"
            style={{
              lineHeight: "1.55em",
              letterSpacing: "normal",
              textWrap: "pretty",
            }}
          >
            {getLocalization("introContent")}
          </Text>
        </Flex>
      </Flex>
      <Flex
        className={`section ${styles.mainContentSectionOuter}`}
        width="100%"
        justify="center"
        align="center"
      >
        <Flex
          gap="7"
          direction="column"
          width="100%"
          px="4"
          className={styles.mainContentSection}
        >
          <Flex
            wrap="wrap"
            width="100%"
            gap="4"
            justify={{ initial: "center", md: "between" }}
            direction={{ initial: "column", md: "row" }}
          >
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/miami-ortho-13.jpg"
                label={getLocalization("whyChooseUs")}
                to="/why-choose-us"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/same-day.jpg"
                label={getLocalization("yourFirstVisit")}
                to="/your-first-visit"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/miami-ortho-9.jpg"
                label={getLocalization("freeConsultation")}
                to="/free-consult"
              />
            </Box>
            <Box flexBasis={{ initial: "0", md: "49%" }}>
              <HoverableImageLink
                imageUrl="/miami-ortho-7.jpg"
                label={getLocalization("insuranceAndFinance")}
                to="/insurance-finances"
              />
            </Box>
          </Flex>
        </Flex>
      </Flex>
      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
